"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AllegroProjectMetadataKey = exports.AllegroFileType = void 0;
exports.getAllegroFileType = getAllegroFileType;
exports.validateAllegroFile = validateAllegroFile;
exports.detectAllegroProjects = detectAllegroProjects;
const project_detection_1 = require("../project-detection");
const cpmParser_1 = require("./parsers/cpm/cpmParser");
const cdsLibParser_1 = require("./parsers/lib/cdsLibParser");
const AllegroNetlistParser_1 = require("./parsers/allegro-netlist/AllegroNetlistParser");
const AllegroPartListParser_1 = require("./parsers/allegro-netlist/AllegroPartListParser");
const cdszArchive_1 = require("./parsers/cdsz/cdszArchive");
var AllegroFileType;
(function (AllegroFileType) {
    AllegroFileType["Cpm"] = "allegro-cpm";
    AllegroFileType["CdsLib"] = "allegro-cds-lib";
    AllegroFileType["Sdax"] = "allegro-sdax";
    AllegroFileType["Xcon"] = "allegro-xcon";
    AllegroFileType["Ascii"] = "allegro-ascii";
    AllegroFileType["Css"] = "allegro-css";
    AllegroFileType["Cdsz"] = "allegro-cdsz";
    AllegroFileType["ChipsPrt"] = "allegro-chips-prt";
    AllegroFileType["Style"] = "allegro-style";
    AllegroFileType["ExpandedNetlist"] = "allegro-expanded-netlist";
    AllegroFileType["ExpandedPartList"] = "allegro-expanded-partlist";
    AllegroFileType["Unknown"] = "allegro-unknown";
})(AllegroFileType || (exports.AllegroFileType = AllegroFileType = {}));
var AllegroProjectMetadataKey;
(function (AllegroProjectMetadataKey) {
    AllegroProjectMetadataKey["FileType"] = "file-type";
    AllegroProjectMetadataKey["ParsedCpm"] = "parsed-cpm";
    AllegroProjectMetadataKey["ParsedCdsLib"] = "parsed-cds-lib";
    AllegroProjectMetadataKey["ParsedNetlist"] = "parsed-netlist";
    AllegroProjectMetadataKey["ParsedPartList"] = "parsed-partlist";
})(AllegroProjectMetadataKey || (exports.AllegroProjectMetadataKey = AllegroProjectMetadataKey = {}));
const CDS_LIB_BASENAME = 'cds.lib';
function fileContentsToString(fileContents) {
    if (typeof fileContents === 'string') {
        return fileContents;
    }
    return new TextDecoder().decode(fileContents);
}
function getAllegroFileType(filename, content) {
    const lower = filename.toLowerCase();
    const basename = lower.split('/').pop() ?? lower;
    if (basename === CDS_LIB_BASENAME) {
        return AllegroFileType.CdsLib;
    }
    const lastDot = basename.lastIndexOf('.');
    if (lastDot < 0) {
        return false;
    }
    const ext = basename.slice(lastDot);
    switch (ext) {
        case '.cpm':
            return AllegroFileType.Cpm;
        case '.sdax':
            return AllegroFileType.Sdax;
        case '.xcon':
            return AllegroFileType.Xcon;
        case '.ascii':
            return AllegroFileType.Ascii;
        case '.cdsz':
            return AllegroFileType.Cdsz;
        case '.css':
            return AllegroFileType.Css;
        case '.style':
            return AllegroFileType.Style;
        case '.prt':
            if (basename === 'chips.prt') {
                return AllegroFileType.ChipsPrt;
            }
            return false;
        case '.dat':
            if (content !== undefined) {
                if ((0, AllegroNetlistParser_1.isAllegroExpandedNetlist)(content)) {
                    return AllegroFileType.ExpandedNetlist;
                }
                if ((0, AllegroPartListParser_1.isAllegroExpandedPartList)(content)) {
                    return AllegroFileType.ExpandedPartList;
                }
                return false;
            }
            if (basename === 'pstxnet.dat') {
                return AllegroFileType.ExpandedNetlist;
            }
            if (basename === 'pstxprt.dat') {
                return AllegroFileType.ExpandedPartList;
            }
            return false;
    }
    return false;
}
function validateAllegroFile(filename, contents) {
    const type = getAllegroFileType(filename, typeof contents === 'string' ? contents : undefined);
    if (type === false) {
        return {
            isValid: false,
            errors: [`Not a recognized Allegro X System Capture file: ${filename}`],
        };
    }
    const semanticType = semanticTypeFor(type);
    const metadata = {
        [AllegroProjectMetadataKey.FileType]: type,
    };
    switch (type) {
        case AllegroFileType.Cpm: {
            const parsed = (0, cpmParser_1.parseCpm)(fileContentsToString(contents));
            if (parsed.designName === '' || parsed.designDb === '') {
                return {
                    isValid: false,
                    errors: [`.cpm is missing required fields (design_name, design_db) in ${filename}`],
                };
            }
            metadata[AllegroProjectMetadataKey.ParsedCpm] = parsed;
            break;
        }
        case AllegroFileType.CdsLib: {
            const parsed = (0, cdsLibParser_1.parseCdsLib)(fileContentsToString(contents));
            if (parsed.defines.size === 0 && parsed.includes.length === 0) {
                return {
                    isValid: false,
                    errors: [`cds.lib is empty or unparseable in ${filename}`],
                };
            }
            metadata[AllegroProjectMetadataKey.ParsedCdsLib] = parsed;
            break;
        }
        case AllegroFileType.ExpandedNetlist: {
            const parseResult = (0, AllegroNetlistParser_1.parseAllegroNetlist)(fileContentsToString(contents));
            if (!parseResult.netlist.nets.length) {
                return { isValid: false, errors: ['No nets found in EXPANDEDNETLIST file'] };
            }
            metadata[AllegroProjectMetadataKey.ParsedNetlist] = parseResult;
            metadata['tool'] = 'OrCAD Allegro Packager-XL';
            metadata['netCount'] = parseResult.netlist.nets.length;
            metadata['nodeCount'] = parseResult.netlist.nets.reduce((sum, net) => sum + net.nodes.length, 0);
            if (parseResult.netlist.headerComment) {
                metadata['headerComment'] = parseResult.netlist.headerComment;
            }
            break;
        }
        case AllegroFileType.ExpandedPartList: {
            const parseResult = (0, AllegroPartListParser_1.parseAllegroPartList)(fileContentsToString(contents));
            if (!parseResult.partList.parts.length) {
                return { isValid: false, errors: ['No parts found in EXPANDEDPARTLIST file'] };
            }
            metadata[AllegroProjectMetadataKey.ParsedPartList] = parseResult;
            metadata['tool'] = 'OrCAD Allegro Packager-XL';
            metadata['partCount'] = parseResult.partList.parts.length;
            if (parseResult.partList.headerComment) {
                metadata['headerComment'] = parseResult.partList.headerComment;
            }
            break;
        }
        case AllegroFileType.Cdsz: {
            if (typeof contents === 'string') {
                return {
                    isValid: false,
                    errors: [`.cdsz archives require binary contents to validate: ${filename}`],
                };
            }
            let extracted;
            try {
                extracted = (0, cdszArchive_1.extractCdsz)(contents);
            }
            catch (e) {
                return {
                    isValid: false,
                    errors: [`.cdsz archive is not a readable ZIP: ${filename}`, e instanceof Error ? e.message : String(e)],
                };
            }
            const netlistEntry = (0, cdszArchive_1.findCdszEntryByBasename)(extracted, 'pstxnet.dat');
            if (netlistEntry) {
                const netlistResult = (0, AllegroNetlistParser_1.parseAllegroNetlist)((0, cdszArchive_1.readCdszEntryAsText)(netlistEntry));
                if (!netlistResult.netlist.nets.length) {
                    return { isValid: false, errors: ['No nets found in EXPANDEDNETLIST inside .cdsz'] };
                }
                metadata[AllegroProjectMetadataKey.ParsedNetlist] = netlistResult;
                metadata['tool'] = 'OrCAD Allegro Packager-XL';
                metadata['netCount'] = netlistResult.netlist.nets.length;
                metadata['nodeCount'] = netlistResult.netlist.nets.reduce((sum, net) => sum + net.nodes.length, 0);
                if (netlistResult.netlist.headerComment) {
                    metadata['headerComment'] = netlistResult.netlist.headerComment;
                }
                const partListEntry = (0, cdszArchive_1.findCdszEntryByBasename)(extracted, 'pstxprt.dat');
                if (partListEntry) {
                    const partListResult = (0, AllegroPartListParser_1.parseAllegroPartList)((0, cdszArchive_1.readCdszEntryAsText)(partListEntry));
                    if (partListResult.partList.parts.length) {
                        metadata[AllegroProjectMetadataKey.ParsedPartList] = partListResult;
                        metadata['partCount'] = partListResult.partList.parts.length;
                    }
                }
            }
            break;
        }
        case AllegroFileType.Sdax:
        case AllegroFileType.Xcon:
        case AllegroFileType.Ascii:
        case AllegroFileType.Css:
        case AllegroFileType.ChipsPrt:
        case AllegroFileType.Style:
            break;
        case AllegroFileType.Unknown:
            break;
    }
    return {
        isValid: true,
        data: {
            semanticType,
            fileFormat: {
                cadTool: 'allegro',
                type,
            },
            referencedFiles: [],
            metadata,
        },
    };
}
function semanticTypeFor(type) {
    switch (type) {
        case AllegroFileType.Cpm:
            return 'project';
        case AllegroFileType.CdsLib:
            return 'lib';
        case AllegroFileType.Sdax:
            return 'schematic';
        case AllegroFileType.Xcon:
            return 'netlist';
        case AllegroFileType.ExpandedNetlist:
            return 'netlist';
        case AllegroFileType.ExpandedPartList:
            return 'bom';
        case AllegroFileType.Ascii:
        case AllegroFileType.Cdsz:
        case AllegroFileType.Css:
        case AllegroFileType.ChipsPrt:
        case AllegroFileType.Style:
        case AllegroFileType.Unknown:
            return 'other';
    }
}
const matchCpm = (0, project_detection_1.endsWithExt)('.cpm');
const matchSdax = (0, project_detection_1.endsWithExt)('.sdax');
function detectAllegroProjects(files, fileDetails) {
    return (0, project_detection_1.detectAnchoredProjects)({
        tool: 'allegro',
        toolDisplayName: 'Allegro X System Capture',
        files,
        fileDetails,
        primaryAnchorMatch: matchCpm,
        primaryAnchorLabel: '.cpm',
        secondaryAnchorMatch: matchSdax,
        secondaryAnchorLabel: '.sdax',
    });
}
//# sourceMappingURL=allegroValidation.js.map